import type {
  BrandWarrantyQuotationItemDto,
  MachineAttachmentDto,
  MachineAttachmentItemDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineDto,
  MachineItemPriceOnlyDto,
  MachineQuotationItemDto,
  WarrantyDto
} from '@api/machines'
import {
  ARCHIVED_ITEMS,
  displayOldPrice,
  formatPriceAndCurrency,
  Options,
  ServicesTypes
} from '@common/utils'
import {
  type IconColor,
  type IconVariant,
  MolsonAccordion,
  MolsonAutocomplete,
  MolsonBox,
  MolsonIcon,
  MolsonRadioButton,
  MolsonRadioGroup,
  MolsonTextField,
  MolsonTypography
} from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { ArchivedNotification } from '../archived-notification/ArchivedNotification'
import { WARRANTY_TYPES } from '../constants'
import { MolsonAutoCompleteTreeView } from '../molson-autocomplete-group-view/MolsonAutocompleteTreeView'
import type { HandleOptionChangeFunctionProps } from '../types'

export type WarrantyProps = {
  expandedServicesSections?: Set<string>
  handleOptionChange: HandleOptionChangeFunctionProps
  machine: MachineAttachmentDto | MachineDto
  machineQuotation: MachineAttachmentItemDto | MachineQuotationItemDto
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  sectionError?: boolean
  translationsPrefix: string
  toggleExpandServicesSection?: (key: string) => void
  useBrandWarranty?: boolean
  brandWarrantyPrices?: BrandWarrantyQuotationItemDto[]
}

export const Warranty = ({
  expandedServicesSections,
  handleOptionChange,
  machine,
  machineQuotation,
  quotationItemOldPrices,
  sectionError,
  translationsPrefix,
  useBrandWarranty,
  toggleExpandServicesSection,
  brandWarrantyPrices
}: WarrantyProps) => {
  const theme = molsonUseTheme()
  const { t } = useTranslation('', {
    keyPrefix: translationsPrefix
  })

  const brandWarrantyKey = useBrandWarranty
    ? 'brandWarrantyItem'
    : 'warrantyItem'

  const initialType =
    machineQuotation?.[brandWarrantyKey]?.typeOfWarranty ??
    WARRANTY_TYPES.STANDARD
  const [selectedWarrantyType, setSelectedWarrantyType] = useState(initialType)

  const [isChangingType, setIsChangingType] = useState(false)

  const itemKey = `${ARCHIVED_ITEMS.WARRANTY}_${machineQuotation?.[brandWarrantyKey]?.id}`

  const [expanded, setExpanded] = useState(
    expandedServicesSections?.has(ServicesTypes.WARRANTY) || sectionError
  )

  const {
    attachmentId,
    equipmentId,
    quoteId: quoteIdParam,
    specId: specIdParam
  } = useParams()

  const quoteId = quoteIdParam ?? specIdParam

  const selectedWarrantyDnp = machineQuotation?.[brandWarrantyKey]?.dnp

  const [warranties, setWarranties] = useState<
    BrandWarrantyQuotationItemDto[] | WarrantyDto[]
  >([])
  const [searchTerm, setSearchTerm] = useState('')

  const selectedWarrantyOldDnp =
    quotationItemOldPrices[brandWarrantyKey]?.dnp ?? undefined

  const displayPriceDifference = displayOldPrice(
    selectedWarrantyDnp,
    selectedWarrantyOldDnp
  )

  const getWarrantiesOptions = (warrantyType: string) => {
    if (!warranties) {
      return []
    }

    return warranties.filter(
      (warranty: WarrantyDto) => warranty.typeOfWarranty === warrantyType
    )
  }

  const getWarrantyLabel = (warranty?: WarrantyDto) => {
    if (!warranty) {
      return ''
    }

    return `${warranty.months} Months / ${warranty.hours} Hrs (+${formatPriceAndCurrency(warranty.dnp as number)})`
  }

  useEffect(() => {
    if (
      !isChangingType &&
      machineQuotation?.[brandWarrantyKey]?.typeOfWarranty
    ) {
      const currentType = machineQuotation[brandWarrantyKey].typeOfWarranty
      if (currentType !== selectedWarrantyType) {
        setSelectedWarrantyType(String(currentType))
      }
    }
  }, [
    machineQuotation?.[brandWarrantyKey]?.typeOfWarranty,
    selectedWarrantyType,
    brandWarrantyKey,
    isChangingType,
    machineQuotation
  ])

  useEffect(() => {
    if (isChangingType && !machineQuotation?.[brandWarrantyKey]?.id) {
      setIsChangingType(false)
    }
  }, [
    machineQuotation?.[brandWarrantyKey]?.id,
    isChangingType,
    brandWarrantyKey,
    machineQuotation
  ])

  useEffect(() => {
    if (sectionError) {
      setExpanded(true)
    }
  }, [sectionError])

  useEffect(() => {
    const fetchWarranties = async () => {
      const warrantiesAux =
        (brandWarrantyPrices?.length ?? 0) > 0
          ? brandWarrantyPrices
          : machine.warranties

      const warrantiesWithoutArchived = (warrantiesAux ?? []).filter(
        warranty => !warranty.isArchived
      )
      const warrantySortedByMonths = warrantiesWithoutArchived?.toSorted(
        (a, b) => (a?.months ?? 0) - (b?.months ?? 0)
      )

      if (warrantySortedByMonths) {
        setWarranties(warrantySortedByMonths)

        // Auto-select Extended if it's the only available type for brand warranties
        if (
          useBrandWarranty &&
          !machineQuotation?.[brandWarrantyKey]?.id &&
          warrantySortedByMonths.length > 0
        ) {
          const hasStandard = warrantySortedByMonths.some(
            w => w.typeOfWarranty === WARRANTY_TYPES.STANDARD
          )
          const hasExtended = warrantySortedByMonths.some(
            w => w.typeOfWarranty === WARRANTY_TYPES.EXTENDED
          )

          // If only Extended warranties are available, select Extended
          if (hasExtended && !hasStandard) {
            setSelectedWarrantyType(WARRANTY_TYPES.EXTENDED)
          }
        }
      }
    }

    if (quoteId && machineQuotation?.quotationItemId) {
      fetchWarranties()
    }
  }, [
    quoteId,
    machineQuotation?.quotationItemId,
    brandWarrantyPrices,
    machine?.warranties,
    useBrandWarranty,
    machineQuotation,
    brandWarrantyKey
  ])

  if (!(machine && quoteId)) {
    return null
  }

  const isWarrantyItemArchived = machineQuotation[brandWarrantyKey]?.isArchived

  const getSummaryIcon = () => {
    if (isWarrantyItemArchived) {
      return { icon: 'ERROR_OUTLINE', color: 'error' }
    }
    if (displayPriceDifference) {
      return { icon: 'WARNING', color: 'warning' }
    }
    return undefined
  }

  const summaryIcon = getSummaryIcon()

  const removeCurrentWarranty = () => {
    if (machineQuotation?.[brandWarrantyKey]?.id) {
      setIsChangingType(true)

      if (equipmentId && attachmentId) {
        handleOptionChange({
          machineId: equipmentId,
          option: Options.REMOVE_WARRANTY_WITHIN_MACHINE,
          optionId: machineQuotation?.[brandWarrantyKey]?.id,
          quotationId: quoteId
        })
      } else {
        handleOptionChange({
          option: Options.REMOVE_WARRANTY,
          optionId: machineQuotation?.[brandWarrantyKey]?.id,
          quotationId: quoteId
        })
      }
    }
  }

  return (
    <MolsonAccordion
      onChange={(_, toggle) => {
        toggleExpandServicesSection?.(ServicesTypes.WARRANTY)

        setExpanded(toggle)
      }}
      summaryIcon={summaryIcon?.icon as IconVariant}
      summaryIconColor={summaryIcon?.color as IconColor}
      withTooltip={{
        description: t('includeExtendedWarranty'),
        title: t('warranty')
      }}
      expanded={
        expandedServicesSections?.has(ServicesTypes.WARRANTY) || expanded
      }
      primaryText={t('warranty')}
    >
      <MolsonRadioGroup
        onChange={e => {
          const newType = e.target.value

          if (newType !== selectedWarrantyType) {
            setSelectedWarrantyType(newType)
            removeCurrentWarranty()
          }
        }}
        sx={{
          width: '100%'
        }}
        row={true}
        value={selectedWarrantyType}
      >
        <MolsonBox display="flex" width="100%">
          {getWarrantiesOptions(WARRANTY_TYPES.STANDARD).length > 0 ? (
            <MolsonRadioButton
              formControlLabelProps={{
                label: t('standard'),
                sx: { flex: 0.5 }
              }}
              radioBtnProps={{
                value: WARRANTY_TYPES.STANDARD
              }}
              checked={selectedWarrantyType === WARRANTY_TYPES.STANDARD}
            />
          ) : null}

          {getWarrantiesOptions(WARRANTY_TYPES.EXTENDED).length > 0 ? (
            <MolsonRadioButton
              formControlLabelProps={{
                label: t('extended'),
                sx: { flex: 0.5 }
              }}
              radioBtnProps={{
                value: WARRANTY_TYPES.EXTENDED
              }}
              checked={selectedWarrantyType === WARRANTY_TYPES.EXTENDED}
            />
          ) : null}
        </MolsonBox>
      </MolsonRadioGroup>
      <MolsonBox sx={{ alignItems: 'center', display: 'flex', mt: 5 }}>
        <MolsonAutocomplete
          sx={{
            ...(machineQuotation?.[brandWarrantyKey]?.isArchived && {
              '.MuiAutocomplete-input': {
                color: theme.palette.primary.error,
                textDecoration: 'line-through !important'
              },
              '.MuiFormControl-root': {
                textDecoration: 'line-through !important'
              },
              color: theme.palette.primary.error
            }),
            ...((sectionError ||
              machineQuotation?.[brandWarrantyKey]?.isArchived) && {
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: `${theme.palette.primary.error} !important`
              }
            }),
            width: displayPriceDifference !== undefined ? '100%' : '50%'
          }}
          renderInput={params => {
            return (
              <MolsonBox sx={{ position: 'relative' }}>
                <MolsonTextField
                  placeholder={t('selectWarranty') ?? ''}
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    value:
                      getWarrantyLabel(machineQuotation?.[brandWarrantyKey]) ??
                      ''
                  }}
                  onChange={e => {
                    setSearchTerm(e.target.value)
                  }}
                  error={
                    sectionError ||
                    machineQuotation?.[brandWarrantyKey]?.isArchived
                  }
                />
              </MolsonBox>
            )
          }}
          open={true}
          getOptionLabel={option => {
            if (typeof option === 'string') {
              return option
            }
            return `${option.hours} Hrs (+${formatPriceAndCurrency(option.dnp)})`
          }}
          options={getWarrantiesOptions(selectedWarrantyType)}
          groupBy={option => {
            if (option.months) {
              return option.months.toString()
            }

            return ''
          }}
          renderGroup={params => {
            return (
              <MolsonAutoCompleteTreeView {...params} searchTerm={searchTerm} />
            )
          }}
          value={machineQuotation?.[brandWarrantyKey] ?? ''}
          onChange={(_, newValue) => {
            setIsChangingType(false)

            if (!newValue && machineQuotation?.[brandWarrantyKey]?.id) {
              removeCurrentWarranty()
              return
            }

            const newValueAux = newValue as WarrantyDto
            if (newValueAux?.id) {
              if (equipmentId && attachmentId) {
                handleOptionChange({
                  machineId: equipmentId,
                  option: Options.ADD_WARRANTY_WITHIN_MACHINE,
                  optionId: newValueAux.id,
                  quotationId: quoteId
                })
                return
              }
              handleOptionChange({
                option: Options.ADD_WARRANTY,
                optionId: newValueAux.id,
                quotationId: quoteId
              })
            }
          }}
        />
        {displayPriceDifference !== undefined &&
          machineQuotation?.[brandWarrantyKey]?.id &&
          !sectionError && (
            <MolsonBox
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 0.5,
                width: '100%'
              }}
            >
              <MolsonTypography
                sx={{
                  color: theme.palette.secondary[500],
                  pl: 2,
                  textDecoration: 'line-through'
                }}
              >
                {formatPriceAndCurrency(selectedWarrantyOldDnp)}
              </MolsonTypography>

              <MolsonIcon
                variant={
                  (selectedWarrantyDnp ?? 0) > (selectedWarrantyOldDnp ?? 0)
                    ? 'ARROW_UPWARD'
                    : 'ARROW_DOWNWARD'
                }
                iconColor={
                  (selectedWarrantyDnp ?? 0) > (selectedWarrantyOldDnp ?? 0)
                    ? 'success'
                    : 'error'
                }
              />
              <MolsonTypography
                color={theme.palette.secondary[600]}
                variant="h4"
              >
                {formatPriceAndCurrency(selectedWarrantyDnp)}
              </MolsonTypography>
            </MolsonBox>
          )}
      </MolsonBox>

      {machineQuotation?.[brandWarrantyKey]?.isArchived && (
        <ArchivedNotification id={itemKey} text={t('serviceUnavailable')} />
      )}

      {sectionError && (
        <MolsonTypography variant="error">
          {t('pleaseSelectWarranty')}
        </MolsonTypography>
      )}
    </MolsonAccordion>
  )
}
